import {
  ConversationProvider,
  type DisconnectionDetails,
  useConversation,
} from '@elevenlabs/react';
import {
  AlertCircle,
  Check,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  RotateCcw,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/shadcn/utils';
import { Button } from '@/modules/ui/components/button/Button';
import { Orb } from '@/modules/ui/components/orb/Orb';

import {
  type ConversationAuthorization,
  getConversationAuthorization,
  safeConversationError,
} from './conversationAuthorization';
import { requestMicrophoneAccessIfNeeded } from './microphonePermission';
import {
  getVoiceConversationView,
  isMicrophonePermissionError,
  mergeVoiceConversationLabels,
  shouldShowPostDisconnectActions,
  shouldShowPostDisconnectRestart,
  type VoiceConversationLabels,
} from './voiceConversationState';

interface VoiceConversationWidgetProps {
  authorization: ConversationAuthorization;
  dynamicVariables?: Record<string, string>;
  colors?: [string, string];
  autoStart?: boolean;
  onConnect?: (conversationId: string) => void;
  onDisconnect?: (details: DisconnectionDetails) => void;
  onFinish?: () => void;
  onError?: (error: Error) => void;
  showFinishButton?: boolean;
  startErrorMessage?: string;
  labels?: Partial<VoiceConversationLabels>;
  showInlineErrors?: boolean;
  showErrorToast?: boolean;
  microphoneAccessGranted?: boolean;
  onEndRequested?: (endSession: () => Promise<void>) => void;
}

interface VoiceConversationError {
  kind: 'microphonePermission' | 'connection';
}

export function VoiceConversationWidget(props: VoiceConversationWidgetProps) {
  return (
    <ConversationProvider>
      <VoiceConversationWidgetInner {...props} />
    </ConversationProvider>
  );
}

function VoiceConversationWidgetInner({
  authorization,
  dynamicVariables,
  colors = ['#6b4eff', '#bfaadb'],
  autoStart = false,
  onConnect,
  onDisconnect,
  onFinish,
  onError,
  showFinishButton = true,
  startErrorMessage = 'Failed to start conversation',
  labels: labelOverrides,
  showInlineErrors = false,
  showErrorToast = true,
  microphoneAccessGranted = false,
  onEndRequested,
}: VoiceConversationWidgetProps) {
  const [hasCallEnded, setHasCallEnded] = useState(false);
  const [disconnectReason, setDisconnectReason] = useState<
    DisconnectionDetails['reason'] | null
  >(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [conversationError, setConversationError] =
    useState<VoiceConversationError | null>(null);
  const hasAutoStartedRef = useRef(false);
  const labels = mergeVoiceConversationLabels(labelOverrides);

  const reportError = (error: unknown, toastError = false) => {
    const normalizedError = safeConversationError(
      error,
      'getSignedUrl' in authorization && !isMicrophonePermissionError(error),
    );
    setConversationError({
      kind: isMicrophonePermissionError(error)
        ? 'microphonePermission'
        : 'connection',
    });
    if (toastError && showErrorToast) toast.error(startErrorMessage);
    onError?.(normalizedError);
  };

  const conversation = useConversation({
    micMuted: isMuted,
    onConnect: ({ conversationId }) => {
      setHasCallEnded(false);
      setDisconnectReason(null);
      setConversationError(null);
      onConnect?.(conversationId);
    },
    onDisconnect: (details) => {
      setHasCallEnded(true);
      setDisconnectReason(details.reason);
      setIsMuted(false);
      onDisconnect?.(details);

      if (details.reason === 'error') {
        reportError(new Error(details.message));
      }
    },
    onError: (error) => {
      reportError(error);
    },
  });

  const startConversation = async () => {
    setIsStarting(true);
    setConversationError(null);
    try {
      await requestMicrophoneAccessIfNeeded({
        accessAlreadyGranted: microphoneAccessGranted,
        requestAccess: () =>
          navigator.mediaDevices.getUserMedia({ audio: true }),
      });

      // Request microphone consent before spending a metered call authorization.
      const connection = await getConversationAuthorization(authorization);
      await conversation.startSession({
        ...connection,
        connectionType: 'websocket',
        dynamicVariables: dynamicVariables || undefined,
      });
    } catch (error) {
      reportError(error, true);
    } finally {
      setIsStarting(false);
    }
  };

  useEffect(() => {
    if (!autoStart || hasAutoStartedRef.current) return;
    hasAutoStartedRef.current = true;
    startConversation();
    // autoStart is intentionally only consumed once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const endConversation = async () => {
    await conversation.endSession();
  };

  const isConnected = conversation.status === 'connected';
  const isConnecting = conversation.status === 'connecting' || isStarting;
  const isSpeaking = conversation.isSpeaking;
  const agentState = !isConnected ? null : isSpeaking ? 'talking' : 'listening';
  const view = getVoiceConversationView({
    isConnected,
    isConnecting,
    hasDisconnected: hasCallEnded,
    hasError: conversationError !== null,
  });

  const showPostCallButtons = shouldShowPostDisconnectActions({
    hasDisconnected: hasCallEnded,
    hasFinishHandler: Boolean(onFinish),
  });
  const showPostCallRestart = shouldShowPostDisconnectRestart({
    hasDisconnected: hasCallEnded,
    disconnectReason,
  });
  const errorTitle =
    conversationError?.kind === 'microphonePermission'
      ? labels.microphonePermissionTitle
      : labels.connectionErrorTitle;
  const errorMessage =
    conversationError?.kind === 'microphonePermission'
      ? labels.microphonePermissionMessage
      : labels.connectionErrorMessage;

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="relative aspect-square w-full max-w-md">
        <Orb
          agentState={agentState}
          colors={colors}
          getInputVolume={conversation.getInputVolume}
          getOutputVolume={conversation.getOutputVolume}
        />
        {isConnected && (
          <div
            role="status"
            aria-live="polite"
            className={cn(
              'absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full px-4 py-2 shadow-lg transition-colors',
              isMuted ? 'bg-destructive/10' : 'bg-secondary',
            )}
          >
            {isMuted ? (
              <MicOff className="text-destructive h-4 w-4" />
            ) : (
              <Mic
                className={`h-4 w-4 ${isSpeaking ? 'text-muted-foreground' : 'text-primary'}`}
              />
            )}
            <span className="text-sm font-medium">
              {isMuted
                ? labels.microphoneOff
                : isSpeaking
                  ? labels.agentSpeaking
                  : labels.listening}
            </span>
          </div>
        )}
      </div>

      {showInlineErrors && view === 'error' ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 w-full max-w-lg rounded-xl border p-4"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="text-destructive mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="text-sm font-semibold">{errorTitle}</p>
              <p className="text-muted-foreground mt-1 text-sm">
                {errorMessage}
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              onClick={startConversation}
              variant="primary"
              size="lg"
              className="gap-2"
              loading={isConnecting}
              disabled={isConnecting}
            >
              <RotateCcw className="h-5 w-5" />
              {labels.retry}
            </Button>
          </div>
        </div>
      ) : showPostCallButtons ? (
        <div className="flex flex-wrap justify-center gap-3">
          {showPostCallRestart && (
            <Button
              onClick={startConversation}
              variant="outline"
              size="lg"
              className="gap-2"
              loading={isConnecting}
              disabled={isConnecting}
            >
              <RotateCcw className="h-5 w-5" />
              {labels.restart}
            </Button>
          )}
          {showFinishButton && (
            <Button
              variant="primary"
              size="lg"
              className="gap-2"
              disabled={isConnecting}
              onClick={onFinish}
            >
              <Check className="h-5 w-5" />
              {labels.finish}
            </Button>
          )}
        </div>
      ) : isConnected ? (
        <div className="flex gap-3">
          <Button
            onClick={() => setIsMuted((prev) => !prev)}
            variant="outline"
            size="lg"
            className={cn(
              'gap-2',
              isMuted &&
                'border-destructive text-destructive hover:bg-destructive/5',
            )}
          >
            {isMuted ? (
              <>
                <MicOff className="h-5 w-5" />
                {labels.unmute}
              </>
            ) : (
              <>
                <Mic className="h-5 w-5" />
                {labels.mute}
              </>
            )}
          </Button>
          <Button
            onClick={() => {
              if (onEndRequested) {
                onEndRequested(endConversation);
                return;
              }

              void endConversation();
            }}
            variant="destructive"
            size="lg"
            className="gap-2"
          >
            <PhoneOff className="h-5 w-5" />
            {labels.end}
          </Button>
        </div>
      ) : (
        <Button
          onClick={startConversation}
          variant="primary"
          size="lg"
          className="gap-2"
          loading={isConnecting}
          disabled={isConnecting}
        >
          <Phone className="h-5 w-5" />
          {labels.start}
        </Button>
      )}
    </div>
  );
}
